import { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { User } from '../types/auth';
import { setUserData } from '../utils/encryption';
import { log, error } from '../utils/logger';
import { setAuthToken, logout as apiLogout } from '../services/api';
import { setAccessToken, getAccessToken } from '../services/tokenService';

interface UseAuthReturn {
  isAuthenticated: boolean;
  user: User | null;
  userInfo: any;
  isLoading: boolean;
  login: (accessToken: string) => void;
  logout: () => void;
  validateToken: () => Promise<boolean>;
  authenticatedFetch: (url: string, options?: import('axios').AxiosRequestConfig) => Promise<any>;
}

/**
 * React hook for JWT authentication with automatic token refresh
 */
export const useAuth = (): UseAuthReturn => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!getAccessToken());
  const [user, setUser] = useState<User | null>(null);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Initialize authentication state
   */
  const initializeAuth = useCallback(async () => {
    setIsLoading(true);
    let token = getAccessToken();

    if (!token) {
      token = await authService.silentRefresh();
      if (token) {
        setAccessToken(token);
        setAuthToken(token);
      }
    }
    
    if (token) {
      try {
        const authenticated = await authService.isAuthenticated();
        setIsAuthenticated(authenticated);
        
        if (authenticated) {
          // Get user info from stored JWT token
          const storedUserInfo = authService.getStoredUserInfo();
          setUserInfo(storedUserInfo);
          
          try {
            log('Calling me API to get latest user data...');
            const userData = await authService.getUserInfo();
            if (userData) {
              setUser(userData);
              // Store updated user data in encrypted storage
              setUserData(userData);
              log('User data updated from me API');
            } else {
              // Fallback to encrypted storage if API fails
              import('../utils/encryption').then(async ({ getUserData }) => {
                const encryptedUserData = getUserData();
                if (encryptedUserData) {
                  setUser(encryptedUserData);
                  log('Using cached user data from encrypted storage');
                }
              });
            }
          } catch (apiError: any) {
            error('Error calling /me API:', apiError);
            // Fallback to encrypted storage if API fails
            import('../utils/encryption').then(async ({ getUserData }) => {
              const encryptedUserData = getUserData();
              if (encryptedUserData) {
                setUser(encryptedUserData);
                log('Using cached user data due to API error');
              }
            });
          }
        }
      } catch (err : any) {
        error('Error initializing auth:', err);
        setIsAuthenticated(false);
        setUser(null);
        setUserInfo(null);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login with access token
   */
  const login = useCallback((accessToken: string) => {
    setAccessToken(accessToken);
    setAuthToken(accessToken);
    setIsAuthenticated(true);
    
    // Get user info from the token
    const storedUserInfo = authService.getStoredUserInfo();
    setUserInfo(storedUserInfo);
    
    // Fetch full user data
    authService.getUserInfo().then(userData => {
      setUser(userData);
      
      // Store user data in encrypted storage
      import('../utils/encryption').then(({ setUserData }) => {
        setUserData(userData);
      });
    }).catch(err => {
      error('Error fetching user data after login:', err);
    });
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(() => {
    apiLogout();
    setAccessToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setUserInfo(null);
  }, []);

  /**
   * Validate token
   */
  const validateToken = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authService.validateToken();
      if (result && result.valid) {
        if (result.user) {
          setUser(result.user as any);
          
          // Store user data in encrypted storage
          import('../utils/encryption').then(({ setUserData }) => {
            setUserData(result.user);
          });
        }
        return true;
      }
      return false;
    } catch (err : any) {
      error('Error validating token:', err);
      return false;
    }
  }, []);

  /**
   * Make authenticated API request
   */
  const authenticatedFetch = useCallback(async (url: string, options: import('axios').AxiosRequestConfig = {}): Promise<any> => {
    try {
      return await authService.authenticatedFetch(url, options);
    } catch (err : any) {
      error('Error making authenticated request:', err);
      throw err;
    }
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return {
    isAuthenticated,
    user,
    userInfo,
    isLoading,
    login,
    logout,
    validateToken,
    authenticatedFetch
  };
};

export default useAuth;
